import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  ExpandedHuntPackageRecord,
  HuntTemplate,
  HuntTemplateList,
  HuntTemplateSummary,
  SavedQuery,
} from '@cyborg-monorepo/hunter/common';
import { FilterDescriptor, State } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HuntUrlService } from '../../hunt/overview/hunt-list/hunt-url-service';
import { UpdatedHuntPackage } from '../../hunt/hunt-template/custom-hunt-package/updated-hunt-package-interface';
import { DeepPartial } from 'chart.js/dist/types/utils';

export interface ExtendedState extends State {
  searchTerm?: string;
  archived: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class HuntTemplateService {
  constructor(
    private httpClient: HttpClient,
    private huntUrlService: HuntUrlService,
  ) {}

  private apiUrl: string = environment.api + '/v2';

  public getHuntTemplates(state: ExtendedState): Observable<HuntTemplateList> {
    let params = new HttpParams();

    if (state.filter && state.filter.filters.length > 0)
      params = this.huntUrlService.stateToHttpParams(
        state.filter.filters as FilterDescriptor[],
      );

    params = params.append('page', state.skip / state.take + 1);
    params = params.append('pageSize', state.take);
    params = params.append('sortField', state.sort[0].field);
    params = params.append('sortDir', state.sort[0].dir.toUpperCase());
    params = params.append('archived', state.archived);

    return this.httpClient.get<HuntTemplateList>(
      `${this.apiUrl}/hunt-template`,
      {
        params: params,
      },
    );
  }

  public getAllHuntTemplates(): Observable<Array<HuntTemplateSummary>> {
    return this.httpClient.get<HuntTemplateSummary[]>(
      `${this.apiUrl}/hunt-template/all`,
    );
  }

  public archiveTemplate(id: string): Observable<HuntTemplate> {
    return this.httpClient.get<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}/archive`,
    );
  }

  public unarchiveTemplate(id: string): Observable<HuntTemplate> {
    return this.httpClient.get<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}/unarchive`,
    );
  }

  public deleteSavedQuery(
    id: string,
    query: SavedQuery,
  ): Observable<HuntTemplate> {
    return this.httpClient.put<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}/delete-query`,
      query,
    );
  }

  public createHuntTemplate(
    template: Partial<HuntTemplate>,
  ): Observable<HuntTemplate> {
    return this.httpClient.post<HuntTemplate>(
      `${this.apiUrl}/hunt-template/`,
      template,
    );
  }

  public getHuntTemplateById(id: string): Observable<HuntTemplate> {
    return this.httpClient.get<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}`,
    );
  }

  public getTotalHuntTemplates(): Observable<{ result: number }> {
    return this.httpClient.get<{ result: number }>(
      `${this.apiUrl}/hunt-template/total-hunt-templates`,
    );
  }

  public updateHuntTemplate(
    id: string,
    templateData: Partial<HuntTemplate>,
  ): Observable<HuntTemplate> {
    return this.httpClient.put<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}`,
      templateData,
    );
  }

  public saveQueryToTemplate(
    id: string,
    savedQuery: SavedQuery,
  ): Observable<HuntTemplate> {
    return this.httpClient.put<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}/save-query`,
      savedQuery,
    );
  }

  public addHuntPackagesToTemplate(
    id: string,
    huntPackages:
      | ExpandedHuntPackageRecord[]
      | DeepPartial<UpdatedHuntPackage>[],
  ): Observable<HuntTemplate> {
    return this.httpClient.put<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}/add-hunt-packages`,
      huntPackages,
    );
  }

  public removeHuntPackagesFromTemplate(
    id: string,
    huntPackages: string[],
  ): Observable<HuntTemplate> {
    return this.httpClient.put<HuntTemplate>(
      `${this.apiUrl}/hunt-template/${id}/remove-hunt-packages`,
      huntPackages,
    );
  }
}
